import { ApiError, OfflineError } from '@platform/client';
import { ZodError } from 'zod';

/**
 * What a failed call should say to the person who made it — GEN-27.
 *
 * A content writer whose session had expired was told `no job returned`, because the SDK
 * **resolves** a failed call rather than throwing and `NewGeneration` checked only `data`.
 *
 * Branching is on the error **code**, never on message text: `packages/schema/src/error.ts`
 * makes `type` the contract and the prose explicitly not. An unrecognised code falls back
 * to the server's own message, and so does an unrecognised *error* — where `apps/mobile`'s
 * `describeError` diverged at GEN-96, because an operator reading a raw message is not a
 * learner. A third consumer would move this beside `ApiError` in `packages/client`.
 */
export function describeApiError(error: unknown): string {
  if (error instanceof OfflineError) {
    return `Could not reach the API. Check your connection and try again. (${error.correlationId})`;
  }

  if (error instanceof ApiError) {
    return `${sentence(error)} (${error.correlationId})`;
  }

  // The generated SDK validates a body before it sends it, so a field the API would have
  // refused is refused here first — and a `ZodError`'s own `message` is the serialised
  // issue array, which is what an operator was being shown. There is no correlation id
  // because there was no request. PATH-01, found by the adversary on a 200-character
  // path title.
  if (error instanceof ZodError) return refused(error);

  return error instanceof Error
    ? error.message
    : 'The console hit an error it does not recognize.';
}

/**
 * The first issue, named like the API's own — `sentence` says why one and not eight.
 *
 * No lead-in sentence: the action feed already says which action was refused, and a
 * second "was refused" in front of the field read as two failures. The path drops to its
 * last segment because the SDK validates the whole request, so an issue arrives as
 * `body.title` where the operator is looking at a field called Title.
 */
function refused(error: ZodError): string {
  const issue = error.issues[0];

  if (issue === undefined) return 'That value was refused.';

  const field = issue.path.at(-1);
  const said = bound(issue) ?? issue.message;

  return field === undefined ? said : `${String(field)}: ${said}`;
}

/**
 * The limit, for the two issues that have one.
 *
 * The generated schemas carry no custom messages, so Zod's own default reaches the
 * operator — and for a length failure that default is "Invalid input", which names
 * neither the field's limit nor which direction it was missed in. The bound is on the
 * issue; this is only spending it.
 */
function bound(issue: ZodError['issues'][number]): string | undefined {
  // `origin` narrows with the code, and only a string's bound counts characters — the
  // same two codes carry a number's range, where "characters" would be a lie.
  if (issue.code === 'too_big' && issue.origin === 'string')
    return `no more than ${String(issue.maximum)} characters`;

  if (issue.code === 'too_small' && issue.origin === 'string')
    return `at least ${String(issue.minimum)} characters`;

  return undefined;
}

/**
 * The correlation id rides along on every one of these. It is the handle that ties a
 * screenshot to a log line and a trace, and diagnosing the report this task came from
 * cost three round-trips for want of exactly that.
 */
function sentence(error: ApiError): string {
  switch (error.code) {
    case 'AUTH_TOKEN_EXPIRED':
      return 'Your session expired while this page was open. Sign in again, then upload the file once more.';
    case 'AUTH_TOKEN_MISSING':
      return 'You are signed out. Sign in again, then upload the file once more.';
    case 'AUTH_ROLE_DENIED':
      return 'This account is not allowed to do that. It needs the admin role.';
    default: {
      // The field-level half of a validation failure. One is almost always the whole
      // story, and listing eight is how a message stops being read.
      const field = error.details[0];

      return field === undefined
        ? error.message
        : `${error.message} — ${field.path}: ${field.message}`;
    }
  }
}
